// 첫 화면 — '오늘의 액션 홈' (IA 감사 §4). 경영뷰(가정치 지표) 대신
// "지금 할 일"을 최상단에. 전부 기존 데이터(contracts·contract_sales·deals) 재활용.
//   ① 파이프라인 요약 배너 (시뮬→계약→딜→실적 카운트)
//   ② 액션 큐 (인허가 미완 · 실적 확정 대기 · 달성/만료 임박)
// 경영뷰(전체 현황)는 여기서 강등 — 배너/CTA로만 진입.
import { useMemo } from "react";
import type { ContractRow, ContractSale, Deal } from "@/types";
import { contractProgress, pct, usd } from "@/lib/margin/model";
import { contractRowToModel, saleToModel } from "./convert";

interface ActionItem {
  contractId: string;
  contractName: string;
  country: string;
  kind: "license" | "pending" | "quota" | "expiry";
  label: string;
  detail: string;
  stage: number;
}

const KIND_META: Record<ActionItem["kind"], { tag: string; cls: string }> = {
  license: { tag: "인허가", cls: "bg-risk-tint text-risk" },
  pending: { tag: "확정 대기", cls: "bg-warn-tint text-warn" },
  quota: { tag: "달성 임박", cls: "bg-brass-tint text-brass-deep" },
  expiry: { tag: "만료 임박", cls: "bg-warn-tint text-warn" },
};

const DAY = 86_400_000;

export default function ActionHome({
  contracts,
  sales,
  deals,
  simCount,
  today,
  onOpenContract,
  onOpenAll,
  onOpenSim,
}: {
  contracts: ContractRow[];
  sales: ContractSale[];
  deals: Deal[];
  simCount: number;
  today: string;
  onOpenContract: (id: string, stage: number) => void;
  onOpenAll: () => void;
  onOpenSim: () => void;
}): JSX.Element {
  const activeContracts = useMemo(() => contracts.filter((c) => c.status === "active"), [contracts]);
  const confirmedDealIds = useMemo(() => new Set(sales.map((s) => s.deal_id).filter(Boolean)), [sales]);
  const openDeals = useMemo(() => deals.filter((d) => d.status !== "done" && d.status !== "cancelled"), [deals]);

  const actions = useMemo<ActionItem[]>(() => {
    const out: ActionItem[] = [];
    const t = new Date(today).getTime();
    for (const c of activeContracts) {
      const country = c.country_name ?? c.destination_country ?? "";
      const ctSales = sales.filter((s) => s.contract_id === c.id);
      const pr = contractProgress(contractRowToModel(c), ctSales.map(saleToModel));
      const ctDeals = deals.filter((d) => d.contract_id === c.id);

      // 인허가 미완
      if (!c.license_done && c.license_label) {
        out.push({ contractId: c.id, contractName: c.name, country, kind: "license", label: `${c.license_label} 미완`, detail: "허가 없으면 판매용 통관 불가", stage: 2 });
      }
      // 실적 확정 대기 (완료 딜 미확정)
      const pending = ctDeals.filter((d) => d.status === "done" && !confirmedDealIds.has(d.id)).length;
      if (pending > 0) {
        out.push({ contractId: c.id, contractName: c.name, country, kind: "pending", label: `실적 확정 대기 ${pending}건`, detail: "완료 딜을 실적 원장에 반영", stage: 4 });
      }
      // 달성 임박
      if (pr.ratio != null && pr.ratio >= 0.85 && pr.ratio < 1) {
        out.push({ contractId: c.id, contractName: c.name, country, kind: "quota", label: `쿼터 달성 ${pct(pr.ratio)}`, detail: pr.target != null ? `${usd(pr.sold)}/${usd(pr.target)}대 — 마무리 임박` : "목표 임박", stage: 4 });
      }
      // 만료 임박 (45일 이내)
      if (c.end_date) {
        const days = Math.round((new Date(c.end_date).getTime() - t) / DAY);
        if (days >= 0 && days <= 45) {
          out.push({ contractId: c.id, contractName: c.name, country, kind: "expiry", label: `계약 만료 D-${days}`, detail: `${c.end_date} 종료 — 갱신 검토`, stage: 2 });
        }
      }
    }
    // 인허가·확정대기 먼저
    const order: Record<ActionItem["kind"], number> = { license: 0, pending: 1, expiry: 2, quota: 3 };
    return out.sort((a, b) => order[a.kind] - order[b.kind]);
  }, [activeContracts, sales, deals, confirmedDealIds, today]);

  const steps = [
    { label: "협상 중 · 시뮬", n: simCount, onClick: onOpenSim },
    { label: "진행 중 · 계약", n: activeContracts.length, onClick: undefined },
    { label: "열린 · 선적 딜", n: openDeals.length, onClick: undefined },
    { label: "확정 · 실적", n: sales.length, onClick: onOpenAll },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-graphite">오늘의 워크데스크</h2>
        <p className="mt-1 text-[12px] text-graphite-mut">지금 손이 필요한 일부터. 가격·마진 표준은 좌측 “전체 현황”에서 확인하세요.</p>
      </div>

      {/* 파이프라인 요약 배너 */}
      <div className="grid grid-cols-4 gap-px overflow-hidden rounded-lg border border-line bg-line">
        {steps.map((s, i) => (
          <button
            key={s.label}
            onClick={s.onClick}
            disabled={!s.onClick}
            className={"group relative flex flex-col items-start bg-surface px-4 py-3 text-left " + (s.onClick ? "hover:bg-surface-2" : "cursor-default")}
          >
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-graphite-mut">{s.label}</span>
            <span className="mt-1 font-mono text-[22px] font-medium leading-none tabular-nums text-graphite">{s.n}</span>
            {i < steps.length - 1 && <span className="absolute -right-[7px] top-1/2 z-10 -translate-y-1/2 text-line-strong">›</span>}
          </button>
        ))}
      </div>

      {/* 액션 큐 */}
      <div className="rounded-lg border border-line bg-surface">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h3 className="flex items-center gap-2 text-[13.5px] font-bold tracking-tight text-graphite before:h-3 before:w-[3px] before:rounded-sm before:bg-brass before:content-['']">
            액션 큐
          </h3>
          <span className="font-mono text-[11px] text-graphite-mut">{actions.length}건</span>
        </div>

        {actions.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-graphite">지금 급한 일은 없습니다. 👍</p>
            <p className="mt-1 text-[12px] text-graphite-mut">
              새 협상을 시작하려면{" "}
              <button onClick={onOpenSim} className="font-semibold text-brass-deep underline decoration-line hover:text-brass">
                협상 시뮬
              </button>
              , 전체 지표는{" "}
              <button onClick={onOpenAll} className="font-semibold text-brass-deep underline decoration-line hover:text-brass">
                전체 현황
              </button>
              에서.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {actions.map((a, i) => {
              const m = KIND_META[a.kind];
              return (
                <li key={a.contractId + a.kind + i}>
                  <button onClick={() => onOpenContract(a.contractId, a.stage)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2">
                    <span className={"shrink-0 rounded px-2 py-0.5 text-[10px] font-bold " + m.cls}>{m.tag}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-semibold text-graphite">{a.label}</span>
                      <span className="block truncate text-[11px] text-graphite-mut">
                        {a.contractName} · {a.detail}
                      </span>
                    </span>
                    <span className="shrink-0 text-line-strong transition-colors group-hover:text-graphite-mut">›</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 진입 CTA */}
      <div className="flex flex-wrap gap-2">
        <button onClick={onOpenSim} className="rounded-md border border-ink bg-ink px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-soft">
          협상 시뮬 시작
        </button>
        <button onClick={onOpenAll} className="rounded-md border border-line bg-surface px-3.5 py-2 text-sm font-semibold text-graphite transition-colors hover:bg-surface-2">
          전체 현황 · 경영뷰 열기
        </button>
      </div>
    </div>
  );
}
